import React, { useEffect, useState } from 'react';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';
import { Toaster } from 'react-hot-toast';
import { useThemeStore } from '../../lib/themeStore';
import { useEmbedStore } from '../../lib/embedStore';
import { useEmbedAutoResize } from '../../lib/hooks/useEmbedAutoResize';
import { useEmbedRecovery } from '../../lib/hooks/useEmbedRecovery';
import { PwaInstallModal } from '../Modals/PwaInstallModal';
import { InactivityIndicator } from '../InactivityIndicator/InactivityIndicator';
import { useLocation } from 'react-router-dom';

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { theme, toggleTheme } = useThemeStore();
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  useEmbedAutoResize();
  useEmbedRecovery();
  const location = useLocation();
  
  // Ouverte par défaut sur desktop (≥1200px), fermée sur mobile/tablette
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1200;
    }
    return true;
  });

  // Pages où la sidebar ne doit PAS être affichée
  const hideSidebarPaths = ['/', '/auth', '/dashboard', '/create-league', '/select-league', '/join-league'];
  const showSidebar = !hideSidebarPaths.includes(location.pathname);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Gérer le redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      // Auto-ouvrir sur desktop, auto-fermer sur mobile/tablette
      if (window.innerWidth >= 1200) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Mode embed générique (?embed=1, vraie iframe) uniquement : afficher juste la barre de recherche de joueur.
  // is_bpm_session seul (persisté en storage) ne doit PAS déclencher ce layout hors iframe, sinon l'app
  // standalone reste bloquée sans Navbar/bouton de déconnexion.
  if (isEmbed) {
    return (
      <div className="embed-content-wrapper" style={{
        width: '100%',
        background: 'transparent',
        margin: 0,
        padding: '0.75rem'
      }}>
        {children}
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: theme === 'dark'
                ? 'rgba(30, 41, 59, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${theme === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
              borderRadius: '1rem',
              fontSize: '0.9rem',
              fontWeight: '500',
              zIndex: 99999
            },
            success: { iconTheme: { primary: '#22c55e', secondary: '#ffffff' } },
            error: { iconTheme: { primary: '#ef4444', secondary: '#ffffff' } }
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-vh-100 position-relative" style={{
      width: '100%',
      minHeight: '100dvh',
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 50%, #1e40af 75%, #2563eb 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #cbd5e1 50%, #3b82f6 75%, #2563eb 100%)',
      margin: 0,
      padding: 0
    }}>
      {/* Animated Background Elements */}
      <div className="position-fixed w-100 h-100" style={{ top: 0, left: 0, zIndex: -1, width: '100%', height: '100dvh' }}>
        <div 
          className="position-absolute rounded-circle"
          style={{
            width: '400px',
            height: '400px',
            background: theme === 'dark' 
              ? 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
            top: '20%',
            right: '10%',
            animation: 'float 10s ease-in-out infinite',
            opacity: 0.6
          }}
        ></div>
        <div 
          className="position-absolute rounded-circle"
          style={{
            width: '300px',
            height: '300px',
            background: theme === 'dark'
              ? 'radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(249, 115, 22, 0.04) 0%, transparent 70%)',
            bottom: '20%',
            left: '5%',
            animation: 'float 12s ease-in-out infinite reverse',
            opacity: 0.5
          }}
        ></div>
      </div>

      {/* Sidebar (affichée seulement sur certaines pages) */}
      {showSidebar && <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />}

      <Navbar />
      {/* Indicateur d'inactivité */}
      <InactivityIndicator />
      
      <PwaInstallModal language="fr" />
      <div 
        className="container-fluid px-3 px-md-4" 
        style={{ 
          paddingTop: '100px',
          marginLeft: showSidebar && sidebarOpen && window.innerWidth >= 1200 ? '280px' : '0',
          transition: 'margin-left 0.3s ease',
          width: showSidebar && sidebarOpen && window.innerWidth >= 1200 ? 'calc(100% - 280px)' : '100%',
        }}
      >
        <div className="row justify-content-center">
          <div className="col-12 col-xl-11">
            <div className="py-4">
              {children}
            </div>
          </div>
        </div>
      </div>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: theme === 'dark' 
              ? 'rgba(30, 41, 59, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
            borderRadius: '1rem',
            fontSize: '0.9rem',
            fontWeight: '500',
            zIndex: 99999
          },
          success: { iconTheme: { primary: '#22c55e', secondary: '#ffffff' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#ffffff' } }
        }}
      />
    </div>
  );
}